import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './DrawableMannequin.css';

// Body part regions for both views
const bodyRegions = {
    front: {
        head: { x: 85, y: 10, w: 30, h: 40 },
        neck: { x: 90, y: 50, w: 20, h: 15 },
        chest: { x: 70, y: 65, w: 60, h: 50 },
        l_arm: { x: 130, y: 65, w: 25, h: 60 },
        r_arm: { x: 45, y: 65, w: 25, h: 60 },
        l_forearm: { x: 135, y: 125, w: 25, h: 50 },
        r_forearm: { x: 40, y: 125, w: 25, h: 50 },
        abdomen: { x: 75, y: 115, w: 50, h: 45 },
        l_thigh: { x: 105, y: 160, w: 20, h: 60 },
        r_thigh: { x: 75, y: 160, w: 20, h: 60 },
        l_calf: { x: 107, y: 220, w: 16, h: 60 },
        r_calf: { x: 77, y: 220, w: 16, h: 60 },
    },
    back: {
        head: { x: 85, y: 10, w: 30, h: 40 },
        nuca: { x: 90, y: 50, w: 20, h: 15 },
        upper_back: { x: 70, y: 65, w: 60, h: 50 },
        l_shoulder: { x: 120, y: 65, w: 30, h: 30 },
        r_shoulder: { x: 50, y: 65, w: 30, h: 30 },
        lower_back: { x: 75, y: 115, w: 50, h: 45 },
        l_thigh: { x: 105, y: 160, w: 20, h: 60 },
        r_thigh: { x: 75, y: 160, w: 20, h: 60 },
        l_calf: { x: 107, y: 220, w: 16, h: 60 },
        r_calf: { x: 77, y: 220, w: 16, h: 60 },
    }
};

const DrawableMannequin = ({ onAreaChange, heightCm, initialData, readOnly = false }) => {
    const { t } = useTranslation();
    const frontCanvasRef = useRef(null);
    const backCanvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [activeCanvas, setActiveCanvas] = useState(null); // 'front' or 'back'
    const [frontPixels, setFrontPixels] = useState([]);
    const [backPixels, setBackPixels] = useState([]);
    const [brushSize, setBrushSize] = useState(5);
    const [currentAreas, setCurrentAreas] = useState([]);

    // Load initial data if provided
    useEffect(() => {
        if (initialData) {
            try {
                const data = typeof initialData === 'string' ? JSON.parse(initialData) : initialData;
                if (data.front) setFrontPixels(data.front);
                if (data.back) setBackPixels(data.back);
            } catch (e) {
                console.error("Error loading mannequin data", e);
            }
        }
    }, [initialData]);

    const drawMannequin = useCallback((ctx, view, pixels) => {
        ctx.strokeStyle = '#555';
        ctx.lineWidth = 2;
        ctx.fillStyle = '#333';

        if (view === 'front') {
            ctx.beginPath();
            ctx.arc(100, 30, 15, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();

            ctx.fillStyle = '#555';
            ctx.beginPath();
            ctx.arc(93, 27, 2, 0, Math.PI * 2);
            ctx.arc(107, 27, 2, 0, Math.PI * 2);
            ctx.fill();

            ctx.fillStyle = '#1a1a1a';
            ctx.fillRect(92, 45, 16, 20);

            ctx.beginPath();
            ctx.moveTo(70, 65);
            ctx.lineTo(130, 65);
            ctx.lineTo(125, 115);
            ctx.lineTo(110, 160);
            ctx.lineTo(90, 160);
            ctx.lineTo(75, 115);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();

            ctx.fillRect(130, 65, 15, 110);
            ctx.fillRect(55, 65, 15, 110);
            ctx.fillRect(105, 160, 18, 120);
            ctx.fillRect(77, 160, 18, 120);
        } else {
            ctx.fillStyle = '#151515';
            ctx.beginPath();
            ctx.arc(100, 30, 15, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();

            ctx.strokeStyle = '#666';
            ctx.beginPath();
            ctx.arc(100, 25, 12, 0.2, Math.PI - 0.2);
            ctx.stroke();
            ctx.strokeStyle = '#555';

            ctx.fillRect(92, 45, 16, 20);

            ctx.beginPath();
            ctx.moveTo(70, 65);
            ctx.lineTo(130, 65);
            ctx.lineTo(125, 115);
            ctx.lineTo(110, 160);
            ctx.lineTo(90, 160);
            ctx.lineTo(75, 115);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();

            ctx.strokeStyle = '#222';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(100, 65);
            ctx.lineTo(100, 160);
            ctx.stroke();
            ctx.strokeStyle = '#555';
            ctx.lineWidth = 2;

            ctx.fillRect(130, 65, 15, 110);
            ctx.fillRect(55, 65, 15, 110);
            ctx.fillRect(105, 160, 18, 120);
            ctx.fillRect(77, 160, 18, 120);
        }

        // Draw the saved pixels
        ctx.fillStyle = '#ff0000';
        pixels.forEach(pixel => {
            ctx.beginPath();
            ctx.arc(pixel.x, pixel.y, pixel.size / 2, 0, Math.PI * 2);
            ctx.fill();
        });

    }, []);

    const calculateAreasAndBodyParts = useCallback(() => {
        const allPixels = [...frontPixels, ...backPixels];
        if (allPixels.length === 0) return;

        const calculateForView = (pixels, viewName) => {
            const regions = bodyRegions[viewName];
            const pixelsByPart = {};

            pixels.forEach(pixel => {
                for (const [partId, region] of Object.entries(regions)) {
                    if (pixel.x >= region.x && pixel.x <= region.x + region.w &&
                        pixel.y >= region.y && pixel.y <= region.y + region.h) {
                        if (!pixelsByPart[partId]) pixelsByPart[partId] = [];
                        pixelsByPart[partId].push(pixel);
                        break;
                    }
                }
            });
            return pixelsByPart;
        };

        const frontParts = calculateForView(frontPixels, 'front');
        const backParts = calculateForView(backPixels, 'back');

        const h = parseFloat(heightCm) || 170;
        const pixelToCm = h / 280;

        const areas = [];
        let totalWidthCm = 0;
        let totalHeightCm = 0;

        const processParts = (parts, sideLabel) => {
            for (const [partId, pixels] of Object.entries(parts)) {
                const xs = pixels.map(p => p.x);
                const ys = pixels.map(p => p.y);
                const width = Math.max(brushSize, Math.max(...xs) - Math.min(...xs));
                const height = Math.max(brushSize, Math.max(...ys) - Math.min(...ys));

                const widthCm = Math.max(1, Math.round(width * pixelToCm));
                const heightCm = Math.max(1, Math.round(height * pixelToCm));

                totalWidthCm += widthCm;
                totalHeightCm += heightCm;

                areas.push({
                    part: `${t(`body_part_${partId}`)} (${sideLabel})`,
                    size: `${widthCm}cm x ${heightCm}cm`,
                    pixels: pixels.length
                });
            }
        };

        processParts(frontParts, 'Frente');
        processParts(backParts, 'Costas');

        const bodyParts = areas.map(a => a.part).join(', ');
        const sizes = areas.map(a => `${a.part}: ${a.size}`).join(' | ');

        setCurrentAreas(areas);

        if (onAreaChange) {
            onAreaChange({
                bodyPart: bodyParts,
                size: sizes,
                pixels: allPixels.length,
                areas: areas,
                totalSize: `${totalWidthCm}cm x ${totalHeightCm}cm (total)`,
                mannequinData: { front: frontPixels, back: backPixels }
            });
        }
    }, [frontPixels, backPixels, heightCm, brushSize, t, onAreaChange]);

    useEffect(() => {
        if (readOnly && (frontPixels.length > 0 || backPixels.length > 0)) {
            calculateAreasAndBodyParts();
        }
    }, [frontPixels, backPixels, readOnly, calculateAreasAndBodyParts]);

    // Draw both canvases
    useEffect(() => {
        const frontCanvas = frontCanvasRef.current;
        const backCanvas = backCanvasRef.current;

        if (frontCanvas) {
            const ctx = frontCanvas.getContext('2d');
            ctx.clearRect(0, 0, frontCanvas.width, frontCanvas.height);
            drawMannequin(ctx, 'front', frontPixels);
        }

        if (backCanvas) {
            const ctx = backCanvas.getContext('2d');
            ctx.clearRect(0, 0, backCanvas.width, backCanvas.height);
            drawMannequin(ctx, 'back', backPixels);
        }
    }, [frontPixels, backPixels, drawMannequin]);

    const isInsideMannequin = (x, y, view) => {
        const regions = bodyRegions[view];
        for (const region of Object.values(regions)) {
            if (x >= region.x && x <= region.x + region.w &&
                y >= region.y && y <= region.y + region.h) return true;
        }
        return false;
    };

    const startDrawing = (e, view) => {
        if (readOnly) return;
        e.preventDefault();
        setIsDrawing(true);
        setActiveCanvas(view);
        handleInteract(e, view);
    };

    const stopDrawing = () => {
        setIsDrawing(false);
        setActiveCanvas(null);
        calculateAreasAndBodyParts();
    };

    const handleInteract = (e, view) => {
        if (!isDrawing && e.type !== 'mousedown' && e.type !== 'touchstart') return;
        if (readOnly) return;
        if (activeCanvas && activeCanvas !== view) return; // Only draw on active canvas
        e.preventDefault();

        const canvas = view === 'front' ? frontCanvasRef.current : backCanvasRef.current;
        const setPixels = view === 'front' ? setFrontPixels : setBackPixels;

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
        const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
        if (!clientX || !clientY) return;

        const x = (clientX - rect.left) * scaleX;
        const y = (clientY - rect.top) * scaleY;

        if (isInsideMannequin(x, y, view)) {
            setPixels(prev => [...prev, { x, y, size: brushSize }]);
        }
    };

    const clearDrawing = () => {
        setFrontPixels([]);
        setBackPixels([]);
        if (onAreaChange) {
            onAreaChange({
                bodyPart: '', size: '', pixels: 0, areas: [], totalSize: ''
            });
        }
    };

    return (
        <div className={`drawable-mannequin-container ${readOnly ? 'read-only' : ''}`}>
            {!readOnly && (
                <div className="mannequin-controls">
                    <div className="brush-controls">
                        <label>{t('brush_size') || 'Tamanho'}:</label>
                        <input type="range" min="5" max="30" value={brushSize} onChange={(e) => setBrushSize(parseInt(e.target.value))} />
                        <span>{brushSize}px</span>
                    </div>
                    <button type="button" className="clear-btn" onClick={clearDrawing}>🗑️ {t('clear') || 'Limpar'}</button>
                </div>
            )}

            <div className="dual-canvas-wrapper">
                <div className="canvas-column">
                    <h4 className="canvas-label">👤 {t('view_front') || 'Frente'}</h4>
                    <canvas
                        ref={frontCanvasRef}
                        width={200}
                        height={300}
                        className={`mannequin-canvas ${!readOnly ? 'edit-mode' : ''}`}
                        onMouseDown={(e) => startDrawing(e, 'front')}
                        onMouseMove={(e) => handleInteract(e, 'front')}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={(e) => startDrawing(e, 'front')}
                        onTouchMove={(e) => handleInteract(e, 'front')}
                        onTouchEnd={stopDrawing}
                    />
                </div>

                <div className="canvas-column">
                    <h4 className="canvas-label">🔄 {t('view_back') || 'Costas'}</h4>
                    <canvas
                        ref={backCanvasRef}
                        width={200}
                        height={300}
                        className={`mannequin-canvas ${!readOnly ? 'edit-mode' : ''}`}
                        onMouseDown={(e) => startDrawing(e, 'back')}
                        onMouseMove={(e) => handleInteract(e, 'back')}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={(e) => startDrawing(e, 'back')}
                        onTouchMove={(e) => handleInteract(e, 'back')}
                        onTouchEnd={stopDrawing}
                    />
                </div>
            </div>

            <div className="canvas-hint">
                {readOnly ? (t('viewing_tattoo_area') || 'Visualizando áreas selecionadas') : (t('draw_hint') || 'Desenhe na área onde será a tatuagem')}
            </div>

            {currentAreas.length > 0 && (
                <div className="selected-areas-display">
                    <h4>{t('selected_regions') || 'Partes do Corpo'}:</h4>
                    <ul>
                        {currentAreas.map((area, index) => (
                            <li key={index}><strong>{area.part}</strong>: {area.size}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default DrawableMannequin;
